import { Component, Input, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortType } from "@app/enum/SortType";
import { SpecialUserPermissions } from "@app/enum/SpecialUserPermissions";
import { UserAccessPermissions } from "@app/enum/UserAccessPermissions";
import { AccountUserType, UserStatus, UserType } from "@app/enum/UserType";
import { AccountFeatures } from "@app/enum/account/AccountFeature";
import { AccountStatus } from "@app/enum/account/AccountStatus";
import { IUser } from "@app/interfaces/IUser";
import { UserEditorComponent } from "@app/main/components/resourceManagement/users/userEditor/user-editor.component";
import { LoginService } from "@app/services/login.service.ajs";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { UserService } from "../../../_services/user.service";
import './user-list.component.scss';
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";

@UntilDestroy()
@Component({
    selector: 'customer-user-list',
    template: require('./user-list.component.html')
})
export class CustomerUserList implements OnInit {
    @Input() customerId: number;

    @ViewChild('userEditor') userEditor: UserEditorComponent;
    @ViewChild('resetPasswordTemplate') resetPasswordTemplate: TemplateRef<any>;

    AccountStatus = AccountStatus;

    tableColumns: IVTableColumn<IUser>[];
    tableRows: IVTableRow[];

    loading: boolean;
    selectedUser: IUser;
    users: IUser[] = [];
    tabCount: number = 0;

    dialog: VDialogRef;

    filterTabs: IFilterBarTab<IUser>[];
    filterEvent: IAggregateFilterEvent<IUser>;
    filteredRecords: IUser[];

    constructor(
        private readonly filterBarService: FilterBarService,
        private readonly dialogService: DialogService,
        private readonly loginService: LoginService,
        private readonly navigationService: NavigationService,
        private readonly translationService: TranslationService,
        private readonly userService: UserService,
        private readonly vTableService: VTableService) { }

    ngOnInit() {
        this.customerId = this.navigationService.getRouteParam("customerId");
        this.createTabs();
        this.createTable();
        this.getData();
    }

    selectUser(row: IVTableRow): void {
        this.selectedUser = row.data;
    }

    closeUserEditor(): void {
        this.selectedUser = null;
    }

    saveUser(): void {
        this.userEditor.saveUser();
    }

    deactivateUser(deactivate: boolean): void {
        if (!deactivate || !this.selectedUser.IsActive) {
            return;
        }

        this.toggleStatus();
    }

    activateUser(): void {
        if (this.selectedUser.IsActive) {
            return;
        }

        this.toggleStatus();
    }

    resetPassword(): void {

        this.dialog = this.dialogService.open(this.resetPasswordTemplate, null);
    }

    confirmResetPassword(): void {
        this.loginService.sendForgotPasswordEmail(this.selectedUser.Email).then(() => {
            this.closeDialog();
        });
    }

    closeDialog(): void {
        this.dialog.close();
    }

    onUserSaved(user: IUser): void {
        const userToUpdate: number = this.users.findIndex(u => u.Id === user.Id);

        if (userToUpdate < 0) {
            this.users.push(user);
        } else {
            this.users.splice(userToUpdate, 1, user);
        }

        this.createTableRows();
        this.closeUserEditor();
    }

    addNewUser(): void {
        this.selectedUser = {
            Id: 0,
            CurrentAccountUserMapping: {
                AccountId: this.customerId,
                Status: UserStatus.Active,
                AccountStatus: AccountStatus.Active,
                Type: AccountUserType.User,

                ReadFeatures: AccountFeatures.Default,
                ManageFeatures: AccountFeatures.Default,
                SpecialUserPermissions: SpecialUserPermissions.None,

                AccessMask: UserAccessPermissions.None
            }
        }
    }

    isFleetUser(user: IUser): boolean {
        return user.Type === UserType.Fleet;
    }

    onFilterChanged($event: any): void {

        if (!$event.trigger) {
            return;
        }

        this.filterEvent = $event;

        this.filteredRecords = this.filterEvent.filteredData;
    }

    //--

    private getData(): void {
        if (!this.customerId) {
            console.warn("No CustomerID specified", this.customerId);
            this.loading = false;
            return;
        }

        this.loading = true;

        this.userService.getUsersForAccount(this.customerId)
            .pipe(untilDestroyed(this))
            .subscribe(users => {
                this.users = users;
                this.tabCount = this.users.length;

                this.createTableRows();
                const selectedUserId = this.navigationService.getRouteParam("userId");

                if (selectedUserId) {
                    this.selectUserById(selectedUserId);
                }

                this.loading = false;
            })

    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.users, this.tableColumns);

    }

    private toggleStatus(): void {
        this.loading = true;
        this.userService.toggleInternalUserStatus(this.selectedUser).pipe(untilDestroyed(this)).subscribe({
            next: () => this.closeUserEditor(),
            complete: () => this.loading = false
        });
    }

    private selectUserById(userId: number): void {
        this.selectedUser = this.users.find(user => user.Id === userId);
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "ID", width: 80, sortable: true, sortType: SortType.Number }),
                createCellFunction: user => this.vTableService.createTableCell(user.Id)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Name", sortable: true }),
                createCellFunction: user => this.vTableService.createTableCell(user.CurrentAccountUserMapping.Name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Email", sortable: true }),
                createCellFunction: user => this.vTableService.createTableCell(user.Email)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Type", width: 80, sortable: true }),
                createCellFunction: user => this.vTableService.createEnumCell(AccountUserType, "AccountUserType", user.CurrentAccountUserMapping.Type)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", sortable: true, width: 100 }),
                createCellFunction: user => this.vTableService.createTableCell(user.IsActive ?
                    this.translationService.t("Common.Active") :
                    this.translationService.t("Common.Inactive"))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Notes", sortable: true }),
                createCellFunction: user => {
                    let notes = "";

                    if (user.ResetPassword) {
                        notes += this.translationService.t("FoundationCustomers.PasswordResetRequired")
                    }

                    if (user.FailedLoginAttempts) {
                        notes += this.translationService.t("FoundationCustomers.FailedAttempts", user.FailedLoginAttempts)
                    }

                    return this.vTableService.createTableCell(notes);
                }
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Created" }),
                createCellFunction: user => this.vTableService.createDateCell(user.CurrentAccountUserMapping.Created)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Updated" }),
                createCellFunction: user => this.vTableService.createDateCell(user.CurrentAccountUserMapping.Updated)
            }

        ]
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IUser>("Common.All", () => true));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IUser>("Common.Active", (user) => user.IsActive));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IUser>("Common.Inactive", (user) => !user.IsActive));
    }
}
